/**
 * Попап покупки обертів колеса (owner 2026-09-23: «дуже продаюче ефектне
 * вікно з тарифами»).
 *
 * Рішення, які тут закладені:
 * - Картка пакета САМА є кнопкою: зайвий крок «обери → оплати» ріже конверсію.
 * - Під кожним пакетом стоїть ціна ЗА ОБЕРТ — без неї вигода пакета невидима.
 * - Зверху — рядок про джекпоти: людина купує не «оберт», а шанс на рік.
 */
import { createPortal } from "react-dom";
import { useEffect } from "react";
import { TgEmoji } from "@/components/TgEmoji";
import { popupActionsCol, popupBtnGhost } from "@/lib/popupButtons";

export interface SpinPack {
  slug: string;
  spins: number;
  amount: number;
  title: string;
}

/** Ціна за оберт цілими гривнями: «35 ₴», а не «34,9 ₴» (owner 2026-09-23). */
function perSpin(pack: SpinPack): string {
  return `${Math.round(pack.amount / pack.spins)} ₴ за оберт`;
}

export function WheelPacksPopup({
  open,
  onClose,
  packs,
  onBuy,
  pendingSlug,
}: {
  open: boolean;
  onClose: () => void;
  packs: SpinPack[];
  onBuy: (slug: string) => void;
  pendingSlug?: string | null;
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  // Найвигідніший пакет — той, у якого найнижча ціна за оберт.
  const best = packs.reduce<SpinPack | null>(
    (acc, p) => (acc === null || p.amount / p.spins < acc.amount / acc.spins ? p : acc),
    null,
  );

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      style={{
        height:
          typeof window !== "undefined" &&
          (
            window as unknown as {
              Telegram?: { WebApp?: { viewportStableHeight?: number } };
            }
          ).Telegram?.WebApp?.viewportStableHeight
            ? `${
                (
                  window as unknown as {
                    Telegram: { WebApp: { viewportStableHeight: number } };
                  }
                ).Telegram.WebApp.viewportStableHeight
              }px`
            : "100vh",
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-y-auto rounded-3xl bg-surface p-5 shadow-2xl"
        style={{ maxHeight: "calc(100% - 32px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 text-center">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="⚡" size={44} />
          </div>
          <div className="font-display text-[1.125rem] font-bold leading-tight text-ink">
            Ще оберти
          </div>
          <div className="mt-1.5 text-[0.78125rem] leading-relaxed text-ink-dim [text-wrap:balance]">
            Спробуй удачу й виграй три місяці, пів року або навіть рік
            підписки — усе це може випасти з одного оберту за 49 ₴.
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-2">
          {packs.map((p) => {
            const isBest = best !== null && p.slug === best.slug;
            const busy = pendingSlug === p.slug;
            return (
              <button
                key={p.slug}
                type="button"
                disabled={busy}
                onClick={() => onBuy(p.slug)}
                className={
                  "relative flex items-center gap-3 rounded-2xl p-4 text-left transition " +
                  (isBest
                    ? "bg-primary/[0.07] ring-2 ring-primary active:scale-[0.99]"
                    : "bg-surface-soft border border-line active:scale-[0.99]")
                }
              >
                <span
                  className={
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] font-display text-[1.0625rem] font-bold " +
                    (isBest ? "bg-primary text-white" : "bg-ink/[0.06] text-ink")
                  }
                >
                  {p.spins}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-[0.9375rem] font-bold text-ink">
                    {p.title}
                  </span>
                  <span className="block text-[0.75rem] text-ink-faint">
                    {perSpin(p)}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block font-display text-[1.0625rem] font-bold tabular-nums text-ink">
                    {p.amount} ₴
                  </span>
                  {isBest && (
                    <span className="block font-display text-[0.6875rem] font-bold text-primary">
                      найвигідніше
                    </span>
                  )}
                </span>
                {busy && (
                  <span className="absolute inset-0 flex items-center justify-center rounded-2xl bg-surface/70 font-display text-[0.8125rem] font-bold text-ink-dim">
                    Готуємо рахунок…
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className={popupActionsCol}>
          <button type="button" className={popupBtnGhost} onClick={onClose}>
            Не зараз
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
